import { Channel, EPGProgram } from '../types/iptv';
import { getCurrentListServer } from './managerDns';

/** V9.0.2: servidor do guia = a DNS da lista da conta. Sem lista no aparelho, o servidor decide (epg.php). */
function epgDnsParam(): string {
  const dns = getCurrentListServer();
  return dns ? `&dns=${encodeURIComponent(dns)}` : '';
}

interface EpgCacheEntry {
  programs: EPGProgram[];
  timestamp: number;
}

const epgMemoryCache = new Map<string, EpgCacheEntry>();
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutos em memória
const CACHE_MAX_ENTRIES = 300;

/**
 * Antes as entradas vencidas eram só ignoradas (nunca apagadas) e a programação crescia a sessão inteira.
 * Agora: apaga as vencidas e mantém no máximo CACHE_MAX_ENTRIES (as mais recentes).
 */
function pruneEpgMemoryCache(): void {
  const now = Date.now();
  for (const [key, entry] of epgMemoryCache) {
    if (now - entry.timestamp >= CACHE_TTL_MS) epgMemoryCache.delete(key);
  }
  if (epgMemoryCache.size > CACHE_MAX_ENTRIES) {
    const oldestFirst = Array.from(epgMemoryCache.entries()).sort((a, b) => a[1].timestamp - b[1].timestamp);
    for (let i = 0; i < oldestFirst.length - CACHE_MAX_ENTRIES; i++) epgMemoryCache.delete(oldestFirst[i][0]);
  }
}

/** Libera toda a programação guardada no serviço (pouca memória / começo de vídeo no stick). */
export function clearEpgMemoryCache(): void {
  epgMemoryCache.clear();
}

/**
 * Extrai o ID numérico do stream a partir do canal (id, streamUrl ou qualidades)
 */
export function extractStreamIdFromChannel(channel: Channel): string | null {
  if (!channel) return null;

  // 1. Se o canal tiver qualidades agrupadas (MULTI), PREFERIR SEMPRE FHD ou HD!
  // No Xtream Codes, os fluxos FHD e HD são os principais e contêm o EPG atualizado de hoje,
  // enquanto os fluxos SD costumam ter grades desatualizadas ou incompletas.
  if (channel.qualities && channel.qualities.length > 0) {
    const fhd = channel.qualities.find((q) => q.resolution === 'FHD');
    const hd = channel.qualities.find((q) => q.resolution === 'HD');
    const bestQuality = fhd || hd;
    if (bestQuality?.streamUrl) {
      const match = bestQuality.streamUrl.match(/(?:\/live\/[^\/]+\/[^\/]+\/|\/)([0-9]+)(?:\.m3u8|\.ts|\.mp4|\/|$)/i);
      if (match && match[1]) return match[1];
    }
  }

  // 2. Tentar pelo padrão ch-xtream-12345 (criado a partir do stream principal no import)
  if (channel.id && channel.id.startsWith('ch-xtream-')) {
    const m = channel.id.match(/^ch-xtream-(\d+)/);
    if (m) return m[1];
  }

  // 3. URLs do canal (streamUrl, qualities, backupStreams)
  const urlsToCheck = [
    channel.streamUrl,
    ...(channel.qualities?.map((q) => q.streamUrl) || []),
    ...(channel.backupStreams || []),
  ];

  for (const u of urlsToCheck) {
    if (!u) continue;
    const match = u.match(/(?:\/live\/[^\/]+\/[^\/]+\/|\/)([0-9]+)(?:\.m3u8|\.ts|\.mp4|\/|$)/i);
    if (match && match[1]) {
      return match[1];
    }
  }

  // 4. Se epgId for numérico (ID de stream no Xtream)
  if (channel.epgId && /^\d+$/.test(channel.epgId.trim())) {
    return channel.epgId.trim();
  }

  // 5. Fallback final: dígitos em channel.id
  if (channel.id && !channel.id.includes('m3u') && !channel.id.includes('imp')) {
    const m = channel.id.match(/\d+/);
    if (m && m[0].length < 10) return m[0];
  }

  return null;
}

/**
 * Busca a grade de programação oficial do canal via API HostGator (servidor Fusion)
 */
export async function fetchChannelEPG(
  channel: Channel,
  action: 'table' | 'short' = 'table',
  forceRefresh = false
): Promise<EPGProgram[]> {
  const streamId = extractStreamIdFromChannel(channel);
  if (!streamId) return [];

  const cacheKey = `${streamId}_${action}`;
  const now = Date.now();

  if (!forceRefresh && epgMemoryCache.has(cacheKey)) {
    const entry = epgMemoryCache.get(cacheKey)!;
    if (now - entry.timestamp < CACHE_TTL_MS) {
      return entry.programs;
    }
  }

  try {
    const basePath =
      typeof window !== 'undefined' &&
      window.location.hostname !== 'localhost' &&
      !window.location.hostname.includes('127.0.0.1')
        ? '/api/epg.php'
        : 'https://iptv.mariozeli.com.br/api/epg.php';

    const refreshParam = forceRefresh ? '&refresh=1' : '';
    const url = `${basePath}?stream_id=${encodeURIComponent(streamId)}&action=${action}${epgDnsParam()}${refreshParam}`;

    const res = await fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!res.ok) {
      return [];
    }

    const data = await res.json();
    const rawPrograms = Array.isArray(data?.programs) ? data.programs : [];

    const programs: EPGProgram[] = rawPrograms.map((p: any) => ({
      id: String(p.id),
      channelId: channel.id,
      title: String(p.title || 'Programa'),
      description: String(p.description || ''),
      startTime: String(p.startTime),
      endTime: String(p.endTime),
    }));

    pruneEpgMemoryCache();
    epgMemoryCache.set(cacheKey, {
      programs,
      timestamp: now,
    });

    return programs;
  } catch (err) {
    console.warn('[EPG Service] Falha ao carregar programação do canal:', streamId, err);
    return [];
  }
}

/**
 * Busca em lote a programação atual de uma lista de canais (ex: categoria ativa na grade)
 * Usa o endpoint batch ultra-otimizado do backend PHP (com cache e curl_multi)
 */
export async function fetchBatchEPG(
  channels: Channel[],
  forceRefresh = false
): Promise<Record<string, EPGProgram[]>> {
  if (!Array.isArray(channels) || channels.length === 0) return {};

  const streamIdMap = new Map<string, Channel>();
  const idsToFetch: string[] = [];
  const result: Record<string, EPGProgram[]> = {};
  const now = Date.now();

  for (const ch of channels) {
    const streamId = extractStreamIdFromChannel(ch);
    if (!streamId) continue;
    streamIdMap.set(streamId, ch);

    const cacheKey = `${streamId}_short`;
    const tableCacheKey = `${streamId}_table`;

    if (!forceRefresh) {
      if (epgMemoryCache.has(tableCacheKey) && now - epgMemoryCache.get(tableCacheKey)!.timestamp < CACHE_TTL_MS) {
        result[ch.id] = epgMemoryCache.get(tableCacheKey)!.programs;
        if (ch.epgId) result[ch.epgId] = result[ch.id];
        continue;
      }
      if (epgMemoryCache.has(cacheKey) && now - epgMemoryCache.get(cacheKey)!.timestamp < CACHE_TTL_MS) {
        result[ch.id] = epgMemoryCache.get(cacheKey)!.programs;
        if (ch.epgId) result[ch.epgId] = result[ch.id];
        continue;
      }
    }
    idsToFetch.push(streamId);
  }

  if (idsToFetch.length === 0) {
    return result;
  }

  // Limita a até 50 canais por chamada em lote
  const batchIds = idsToFetch.slice(0, 50);

  try {
    const basePath =
      typeof window !== 'undefined' &&
      window.location.hostname !== 'localhost' &&
      !window.location.hostname.includes('127.0.0.1')
        ? '/api/epg.php'
        : 'https://iptv.mariozeli.com.br/api/epg.php';

    const refreshParam = forceRefresh ? '&refresh=1' : '';
    const url = `${basePath}?action=batch&stream_ids=${batchIds.join(',')}${epgDnsParam()}${refreshParam}`;

    const res = await fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!res.ok) {
      return result;
    }

    const data = await res.json();
    const batchEpg = data?.epg || {};

    for (const [streamId, progs] of Object.entries<any>(batchEpg)) {
      const ch = streamIdMap.get(streamId);
      if (!ch || !Array.isArray(progs)) continue;

      const programs: EPGProgram[] = progs.map((p: any) => ({
        id: String(p.id),
        channelId: ch.id,
        title: String(p.title || 'Programa'),
        description: String(p.description || ''),
        startTime: String(p.startTime),
        endTime: String(p.endTime),
      }));

      pruneEpgMemoryCache();
      epgMemoryCache.set(`${streamId}_short`, {
        programs,
        timestamp: now,
      });

      result[ch.id] = programs;
      if (ch.epgId) {
        result[ch.epgId] = programs;
      }
      // Vincula também aos IDs de todas as qualidades do canal para garantir lookup imediato
      if (ch.qualities && Array.isArray(ch.qualities)) {
        for (const q of ch.qualities) {
          const qm = q.streamUrl.match(/(?:\/live\/[^\/]+\/[^\/]+\/|\/)([0-9]+)(?:\.m3u8|\.ts|\.mp4|\/|$)/i);
          if (qm && qm[1]) {
            result[`ch-xtream-${qm[1]}`] = programs;
            result[qm[1]] = programs;
          }
        }
      }
    }
  } catch (err) {
    console.warn('[EPG Service] Falha ao carregar lote de EPG:', err);
  }

  return result;
}

/**
 * Retorna estritamente os programas ativos (no ar agora) e os próximos dentro das próximas 24 horas.
 * Descarta completamente qualquer programa que já terminou no passado ou além de 24h.
 */
export function getActiveAndUpcomingPrograms(programs: EPGProgram[]): EPGProgram[] {
  if (!Array.isArray(programs) || programs.length === 0) return [];
  const now = Date.now();
  const maxFuture = now + 24 * 60 * 60 * 1000; // Próximas 24 horas
  const PAST_TOLERANCE_MS = 60 * 1000; // 1 min de tolerância para o programa atual no ar

  const valid = programs.filter((prog) => {
    const end = new Date(prog.endTime).getTime();
    const start = new Date(prog.startTime).getTime();
    if (isNaN(end) || isNaN(start)) return false;

    // Descarta se o programa já terminou
    if (end < now - PAST_TOLERANCE_MS) return false;

    // Descarta se o programa começa depois de 24 horas
    if (start > maxFuture) return false;

    return true;
  });

  // Ordena cronologicamente por horário de início
  return valid.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
}

/**
 * Histórico de programas anteriores desativado
 */
export function getPastPrograms(_programs: EPGProgram[]): EPGProgram[] {
  return [];
}

/**
 * Retorna o programa atualmente no ar a partir de uma lista de programas
 */
export function getCurrentProgram(programs: EPGProgram[]): EPGProgram | null {
  if (!Array.isArray(programs) || programs.length === 0) return null;
  const now = Date.now();

  // 1. Procura programa estritamente dentro do horário (com 2 min de tolerância para gaps entre programas)
  const exact = programs.find((prog) => {
    const s = new Date(prog.startTime).getTime();
    const e = new Date(prog.endTime).getTime();
    return !isNaN(s) && !isNaN(e) && now >= s - 120000 && now <= e + 60000;
  });
  if (exact) return exact;

  // 2. Se não encontrar estrito, procura o primeiro programa cujo término é no futuro (programa atual ou próximo imediato)
  const upcoming = programs.find((prog) => {
    const e = new Date(prog.endTime).getTime();
    return !isNaN(e) && e > now;
  });
  if (upcoming) return upcoming;

  // 3. Fallback restrito: apenas aceita o último programa se encerrou há menos de 15 minutos (evita mostrar programas de horas atrás)
  const lastProg = programs[programs.length - 1];
  if (lastProg) {
    const end = new Date(lastProg.endTime).getTime();
    if (!isNaN(end) && end >= now - 15 * 60 * 1000) {
      return lastProg;
    }
  }

  return null;
}

/**
 * Calcula a porcentagem decorrida do programa no ar
 */
export function getProgramProgress(program: EPGProgram | null): number {
  if (!program) return 0;
  const now = Date.now();
  const start = new Date(program.startTime).getTime();
  const end = new Date(program.endTime).getTime();
  if (isNaN(start) || isNaN(end) || end <= start) return 0;
  if (now <= start) return 0;
  if (now >= end) return 100;
  return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
}
